<template>
  <v-container>
    <v-row align="center" dense>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="cnpjFilter"
          label="CNPJ"
          :prepend-inner-icon="mdiMagnify"
          clearable
          hide-details
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="nameFilter"
          label="Razao Social"
          :prepend-inner-icon="mdiMagnify"
          clearable
          hide-details
        />
      </v-col>
      <v-col cols="12" md="3">
        <v-text-field
          v-model="satSerialFilter"
          label="N Serie do SAT"
          :prepend-inner-icon="mdiMagnify"
          clearable
          hide-details
        />
      </v-col>
      <v-col cols="12" md="3" class="text-center text-md-right">
        <v-btn-toggle v-model="filterMode" mandatory density="compact">
          <v-btn value="all" size="large">Todos</v-btn>
          <v-btn value="assigned" size="large">Vinculados</v-btn>
          <v-btn value="unassigned" size="large">Pendentes</v-btn>
        </v-btn-toggle>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-data-table
          :headers="headers"
          :items="stores"
          v-model:items-per-page="itemsPerPage"
          v-model:page="page"
          :items-length="totalItems"
          :loading="isLoading"
          :items-per-page-options="[
            { value: 10, title: '10' },
            { value: 20, title: '20' },
            { value: 40, title: '40' },
            { value: 50, title: '50' },
          ]"
          @click:row="onRowClick"
        >
          <template v-slot:item.loja_cnpj="{ item }">
            {{ formatCnpj(item.loja_cnpj) }}
          </template>

          <template v-slot:item.assigned="{ item }">
            <v-chip size="small" :color="item.assigned ? 'success' : 'warning'">
              {{ item.assigned ? "Vinculado" : "Pendente" }}
            </v-chip>
          </template>

          <template v-slot:item.tenant_name="{ item }">
            {{ item.tenant_name || "-" }}
          </template>

          <template v-slot:item.actions="{ item }">
            <v-btn
              v-if="!item.assigned"
              size="small"
              color="primary"
              @click.stop="onAssignClick(item)"
            >
              Vincular
            </v-btn>
            <v-btn
              size="small"
              icon
              variant="text"
              @click.stop="onDetailsClick(item)"
              title="Ver detalhes"
            >
              <v-icon size="small" :icon="mdiInformation" />
            </v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <!-- Assignment Dialog -->
    <preregistration-assign-dialog
      v-if="selectedStore && showAssignDialog"
      v-model="showAssignDialog"
      :store="selectedStore"
      @success="onAssignSuccess"
    />
  </v-container>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from "vue";
import { mdiMagnify, mdiInformation } from "@mdi/js";
import { useRouter } from "vue-router";
import { usePreregistrationsQuery } from "@/queries/modules/preregistrations";
import PreregistrationAssignDialog from "./PreregistrationAssignDialog.vue";

const router = useRouter();

// State
const page = ref(1);
const itemsPerPage = ref(10);
const cnpjFilter = ref("");
const nameFilter = ref("");
const satSerialFilter = ref("");
const filterMode = ref("all");
const selectedStore = ref(null);
const showAssignDialog = ref(false);
const searchDebounceTimer = ref(null);

// Table headers (Vuetify 3 format)
const headers = [
  { title: "Loja", key: "loja_razao_social", sortable: false },
  { title: "CNPJ", key: "loja_cnpj", sortable: false },
  {
    title: "Dispositivos",
    key: "device_count",
    align: "center",
    sortable: false,
  },
  { title: "Software House/Revenda", key: "tenant_name", sortable: false },
  { title: "Status", key: "assigned", align: "center", sortable: false },
  { title: "", key: "actions", align: "end", sortable: false },
];

// Computed query params
const queryParams = computed(() => {
  const params = {
    limit: itemsPerPage.value,
    offset: (page.value - 1) * itemsPerPage.value,
  };

  // Add filter parameters based on filterMode
  if (filterMode.value === "assigned") {
    params.assigned_only = true;
  } else if (filterMode.value === "unassigned") {
    params.unassigned_only = true;
  }

  // Add text filter parameters (only if non-empty)
  if (cnpjFilter.value) {
    params.loja_cnpj_filter = cnpjFilter.value;
  }
  if (nameFilter.value) {
    params.loja_razao_social_filter = nameFilter.value;
  }
  if (satSerialFilter.value) {
    params.sat_serial_filter = satSerialFilter.value;
  }

  return params;
});

// TanStack Query for pre-registrations
const { data, isLoading, refetch } = usePreregistrationsQuery(queryParams);

// Extract data from query response
const stores = computed(() => data.value || []);

// Calculate total items for pagination
// Since API returns array, we estimate based on results
const totalItems = computed(() => {
  const storeCount = stores.value.length;
  if (storeCount < itemsPerPage.value) {
    return (page.value - 1) * itemsPerPage.value + storeCount;
  }
  // More items might exist, so we add at least one more page
  return page.value * itemsPerPage.value + 1;
});

// Format CNPJ helper
const formatCnpj = (cnpj) => {
  if (!cnpj) return "";
  const cleaned = cnpj.replace(/\D/g, "");
  if (cleaned.length !== 14) return cnpj;
  return cleaned.replace(
    /^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/,
    "$1.$2.$3/$4-$5",
  );
};

// Debounced search
const debouncedRefresh = () => {
  if (searchDebounceTimer.value) {
    clearTimeout(searchDebounceTimer.value);
  }
  searchDebounceTimer.value = setTimeout(() => {
    page.value = 1; // Reset to page 1 when filters change
  }, 500);
};

// Event handlers
const onAssignClick = (store) => {
  selectedStore.value = store;
  showAssignDialog.value = true;
};

const onAssignSuccess = () => {
  refetch();
};

const onDetailsClick = (store) => {
  router.push({
    name: "preregistrations.details",
    params: { loja_cnpj: store.loja_cnpj },
  });
};

const onRowClick = (_, { item }) => {
  onDetailsClick(item);
};

// Watchers for filter changes
watch(filterMode, () => {
  page.value = 1;
});

watch([cnpjFilter, nameFilter, satSerialFilter], () => {
  debouncedRefresh();
});

// Cleanup on unmount
onBeforeUnmount(() => {
  if (searchDebounceTimer.value) {
    clearTimeout(searchDebounceTimer.value);
  }
});
</script>
